import { useEffect, useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Zap, Wrench, Cpu, FileText, CheckCircle2, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/contexts/AuthContext";

const EUR = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

// --- Collapsible Section -----------------------------------------------------
const Section = ({ icon: Icon, title, children, defaultOpen = false, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-zinc-800">
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 py-3 text-left active:scale-[0.99] transition"
      >
        <span className="flex items-center gap-2 text-[14px] font-bold text-zinc-50">
          <Icon size={16} className="text-[#00E575]" />
          {title}
        </span>
        {open ? <ChevronUp size={16} className="text-zinc-300" /> : <ChevronDown size={16} className="text-zinc-300" />}
      </button>
      {open && <div className="pb-4 text-[13px] font-medium leading-relaxed text-zinc-100">{children}</div>}
    </div>
  );
};

// --- Package Card ------------------------------------------------------------
const PackageCard = ({ pkg, isManagerView, selected, onSelect }) => (
  <div
    data-testid={`pkg-card-${pkg.id}`}
    className={`bg-[#18181B] border-2 rounded-2xl overflow-hidden transition ${selected ? "border-[#00E575] shadow-[0_0_20px_rgba(0,229,117,0.18)]" : "border-zinc-800 hover:border-zinc-700"}`}
  >
    {/* Header */}
    <button
      type="button"
      onClick={() => onSelect(pkg.id)}
      data-testid={`pkg-select-${pkg.id}`}
      className="w-full text-left px-4 pt-4 pb-3"
    >
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`font-mono text-[9.5px] tracking-[0.2em] uppercase px-1.5 py-0.5 rounded ${pkg.brand === "LG" ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30"}`}>
              {pkg.brand}
            </span>
            <span className="font-mono text-[12px] text-zinc-400 tracking-[0.2em] uppercase">Wärmepumpe · {pkg.kw} kW</span>
          </div>
          <h3 className="font-display text-lg font-bold leading-tight mt-0.5">{pkg.name}</h3>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 ${selected ? "bg-[#00E575] border-[#00E575]" : "border-zinc-600"}`}>
          {selected && <CheckCircle2 size={14} className="text-[#09090B]" />}
        </div>
      </div>
      <div className="flex items-baseline justify-between mt-2 gap-3 flex-wrap">
        <div>
          <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">
            {isManagerView ? "Abgabepreis Vertrieb (brutto)" : "Verkaufspreis (brutto)"}
          </div>
          <div className="font-display text-2xl font-bold text-[#00E575]">
            {EUR(isManagerView ? pkg.abgabepreis_brutto : pkg.verkaufspreis_brutto)}
          </div>
        </div>
        {isManagerView && pkg.verkaufspreis_brutto !== pkg.abgabepreis_brutto && (
          <div className="text-right">
            <div className="text-[12px] font-mono uppercase tracking-wider text-zinc-300">Mit Marge (Rep-Sicht)</div>
            <div className="font-mono text-sm text-zinc-300">{EUR(pkg.verkaufspreis_brutto)}</div>
          </div>
        )}
      </div>
    </button>

    {/* Collapsible details */}
    <div className="px-4">
      <Section icon={Zap} title="Leistung & technische Daten" testid={`pkg-section-leistung-${pkg.id}`}>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[12.5px]">
          {Object.entries(pkg.leistung).map(([k, v]) => (
            <div key={k} className="flex items-start justify-between gap-2 border-b border-zinc-800/60 py-1.5">
              <dt className="text-zinc-300 font-medium">{k}</dt>
              <dd className="font-mono font-semibold text-zinc-50 text-right tabular-nums">{v}</dd>
            </div>
          ))}
        </dl>
      </Section>
      <Section icon={Wrench} title="Material" testid={`pkg-section-material-${pkg.id}`}>
        <p>{pkg.material}</p>
      </Section>
      <Section icon={Cpu} title="Montage" testid={`pkg-section-montage-${pkg.id}`}>
        <p>{pkg.montage}</p>
      </Section>
      <Section icon={FileText} title="Beratung & Begleitung" testid={`pkg-section-beratung-${pkg.id}`}>
        <p>{pkg.beratung}</p>
      </Section>
    </div>
  </div>
);

// --- Addon Group (Checkboxes) -----------------------------------------------
const AddonGroup = ({ title, icon: Icon, items, selectedIds, onToggle, testid }) => {
  const [open, setOpen] = useState(false);
  const total = items
    .filter((i) => selectedIds.has(i.id))
    .reduce((s, i) => s + (i.brutto || 0), 0);

  return (
    <section data-testid={testid} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 active:scale-[0.99] transition"
      >
        <span className="flex items-center gap-2.5 text-[15px] font-bold text-zinc-50">
          <Icon size={17} className="text-[#00E575]" />
          {title}
        </span>
        <span className="flex items-center gap-2">
          {total > 0 && (
            <span className="font-mono text-xs text-[#00E575] font-bold whitespace-nowrap">{EUR(total)}</span>
          )}
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-zinc-800">
          <ul className="divide-y divide-zinc-800/60">
            {items.map((it) => {
              const sel = selectedIds.has(it.id);
              return (
                <li key={it.id} className="py-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onToggle(it.id)}
                    data-testid={`addon-${it.id}`}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${sel ? "bg-[#00E575] border-[#00E575]" : "border-zinc-500 hover:border-zinc-300"}`}
                  >
                    {sel && <CheckCircle2 size={13} className="text-[#09090B]" />}
                  </button>
                  <span className="flex-1 text-[14px] font-semibold text-zinc-50 leading-snug">{it.name}</span>
                  <span className="font-mono text-[13px] font-bold text-zinc-100 whitespace-nowrap tabular-nums">{EUR(it.brutto)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

// --- Main Page ---------------------------------------------------------------
export default function HeikoCatalog() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(new Set());
  const [addonGroups, setAddonGroups] = useState([]);

  useEffect(() => {
    api.get("/wp-catalog")
      .then((r) => {
        setData(r.data);
        // Compute dynamic addon groups: backend ships `addon_groups`,
        // fall back to legacy hydraulic/electric for older builds.
        const groups = r.data?.addon_groups?.length
          ? r.data.addon_groups
          : [
              { slug: "hydraulic", label: "Hydraulischer Anschluss", items: r.data.hydraulic_addons || [] },
              { slug: "electric",  label: "Elektrischer Anschluss",  items: r.data.electric_addons  || [] },
            ];
        setAddonGroups(groups);
        if (r.data?.packages?.length) {
          // Default-Auswahl: erstes HEIKO 12 kW (Mitte) – falls vorhanden,
          // sonst erstes Paket.
          const def = r.data.packages.find((p) => p.brand === "HEIKO" && p.kw === 12)
                   || r.data.packages[0];
          setSelectedPkg(def.id);
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Katalog konnte nicht geladen werden"))
      .finally(() => setLoading(false));
  }, []);

  const toggleAddon = (id) =>
    setSelectedAddons((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const totals = useMemo(() => {
    if (!data) return { paket: 0, addons: 0, total: 0 };
    const pkg = data.packages.find((p) => p.id === selectedPkg);
    const paket = pkg ? (data.is_manager_view ? pkg.abgabepreis_brutto : pkg.verkaufspreis_brutto) : 0;
    const allItems = addonGroups.flatMap((g) => g.items || []);
    const addons = allItems
      .filter((i) => selectedAddons.has(i.id))
      .reduce((s, i) => s + (i.brutto || 0), 0);
    return { paket, addons, total: paket + addons };
  }, [data, selectedPkg, selectedAddons, addonGroups]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B]">
        <AppHeader />
        <div className="max-w-6xl mx-auto px-4 py-12 flex items-center justify-center">
          <Loader2 className="animate-spin text-[#00E575]" size={28} />
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-8 pb-32">
        <header className="mb-6">
          <p className="font-mono text-xs text-[#00E575] tracking-[0.3em] uppercase mb-2">Paketkatalog</p>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Wärmepumpen-Pakete</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            HEIKO Thermal Plus &amp; LG Therma V R290 — vorkonfigurierte Pakete inkl. Material, Montage und Beratung &amp; Begleitung.
            {data.is_manager_view ? (
              <span className="ml-1 text-amber-300">Manager-Ansicht: Abgabepreise an Vertrieb sichtbar.</span>
            ) : data.margin_applied ? (
              <span className="ml-1 text-zinc-500">Preise inkl. Vertriebsmarge ({data.margin_applied.type === "percent" ? `${data.margin_applied.value}%` : `+${EUR(data.margin_applied.value)}`}).</span>
            ) : null}
          </p>
        </header>

        {/* Package Cards grouped by brand */}
        {["HEIKO", "LG"].map((brand) => {
          const brandPkgs = data.packages.filter((p) => p.brand === brand);
          if (brandPkgs.length === 0) return null;
          return (
            <section key={brand} className="mb-8" data-testid={`brand-section-${brand.toLowerCase()}`}>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="font-display text-xl font-bold tracking-tight">
                  {brand === "HEIKO" ? "HEIKO Thermal Plus" : "LG Therma V R290"}
                </h2>
                <span className={`font-mono text-[12px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full ${brand === "LG" ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/30"}`}>
                  {brandPkgs.length} Pakete
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {brandPkgs.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    isManagerView={data.is_manager_view}
                    selected={pkg.id === selectedPkg}
                    onSelect={setSelectedPkg}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {/* Addon groups */}
        <h2 className="font-display text-xl font-bold mb-3 mt-8 text-zinc-50">Zusatzleistungen</h2>
        <div className="space-y-2 mb-6">
          {addonGroups.map((g) => (
            <AddonGroup
              key={g.slug}
              testid={`addon-group-${g.slug}`}
              title={g.label}
              icon={g.slug === "electric" ? Zap : Wrench}
              items={g.items}
              selectedIds={selectedAddons}
              onToggle={toggleAddon}
            />
          ))}
        </div>
      </main>

      {/* Sticky total summary */}
      <div data-bottom-bar className="fixed bottom-0 left-0 right-0 z-30 bg-[#0E0E12]/95 backdrop-blur-xl border-t border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[12px] font-mono uppercase tracking-widest text-zinc-300">Gesamt brutto</div>
            <div className="font-display text-2xl font-bold text-[#00E575] tabular-nums">{EUR(totals.total)}</div>
            <div className="text-[12px] text-zinc-300 font-mono tabular-nums">
              Paket {EUR(totals.paket)} {totals.addons > 0 && <>· Zusatz {EUR(totals.addons)}</>}
            </div>
          </div>
          <button
            type="button"
            data-testid="catalog-info-btn"
            onClick={() => toast.info("Auswahl in Angebot übernehmen folgt in Kürze.", { description: "Verknüpfung mit dem Angebots-Workflow im Projekt-Detail." })}
            className="shrink-0 inline-flex items-center gap-2 bg-[#00E575] text-[#09090B] font-bold px-4 h-11 rounded-xl active:scale-[0.97] transition glow-green"
          >
            <Sparkles size={16} /> Auswahl notieren
          </button>
        </div>
      </div>
    </div>
  );
}
